import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { SignedDistanceField } from '~/engine/core/models/SignedDistanceField';
import { Grid } from '~/engine/data/common/Grid';
import { getSdfValue } from './SDFVolumeCalculation';

/**
 * Per-ROI geometry statistics derived from the SDF, in internal units
 * (mm / mm² / unitless / patient-space mm). Unit conversion to display units
 * happens at the UI boundary (see docs/ui_specs.md), not here.
 *
 * Volume is intentionally NOT included — it already has a cached getter on
 * ContourTexture (`volumeCm3` ← `calculateVolumeMm3`); these are the
 * additional shape metrics (G10).
 */
export interface RoiStatistics {
    /** Iso-surface area in mm² (marching-tetrahedra at the SDF threshold). */
    surfaceAreaMm2: number;
    /**
     * Sphericity ψ = π^(1/3)·(6V)^(2/3) / SA — 1.0 for a perfect sphere,
     * lower for less compact shapes. Null when V or SA is non-positive.
     */
    sphericity: number | null;
    /** Volume-weighted center of mass in patient-space mm. Null when empty. */
    centerOfMassMm: vec3 | null;
    /** Axis-aligned bounding box (patient-space mm). Null when empty. */
    boundingBox: { min: vec3; max: vec3 } | null;
}

const SDF_THRESHOLD = 127.5;

// Cube corner offsets, indices 0–7 (voxel-local). Standard layout:
// 0:(0,0,0) 1:(1,0,0) 2:(1,1,0) 3:(0,1,0) 4:(0,0,1) 5:(1,0,1) 6:(1,1,1) 7:(0,1,1)
const CORNER_OFFSETS: ReadonlyArray<readonly [number, number, number]> = [
    [0, 0, 0],
    [1, 0, 0],
    [1, 1, 0],
    [0, 1, 0],
    [0, 0, 1],
    [1, 0, 1],
    [1, 1, 1],
    [0, 1, 1],
];

// Decompose each cube cell into 6 tetrahedra, all sharing the main diagonal
// 0–6. Marching tetrahedra over these gives a watertight iso-surface with
// only the simple per-tet cases below (no 256-entry lookup table to vendor).
const CUBE_TETRAHEDRA: ReadonlyArray<
    readonly [number, number, number, number]
> = [
    [0, 1, 2, 6],
    [0, 2, 3, 6],
    [0, 3, 7, 6],
    [0, 7, 4, 6],
    [0, 4, 5, 6],
    [0, 5, 1, 6],
];

/** Linear iso-crossing point between two tet corners at the threshold. */
function interpolate(
    pa: ReadonlyVec3,
    pb: ReadonlyVec3,
    va: number,
    vb: number,
    threshold: number
): vec3 {
    // va !== vb is guaranteed: one corner is > threshold and the other <=.
    const t = (threshold - va) / (vb - va);
    return vec3.fromValues(
        pa[0] + t * (pb[0] - pa[0]),
        pa[1] + t * (pb[1] - pa[1]),
        pa[2] + t * (pb[2] - pa[2])
    );
}

/** Area of the triangle (p0, p1, p2). */
function triangleArea(
    p0: ReadonlyVec3,
    p1: ReadonlyVec3,
    p2: ReadonlyVec3
): number {
    const e1 = vec3.sub(vec3.create(), p1, p0);
    const e2 = vec3.sub(vec3.create(), p2, p0);
    const cross = vec3.cross(vec3.create(), e1, e2);
    return 0.5 * vec3.length(cross);
}

/**
 * Iso-surface area (mm²) of one tetrahedron via marching tetrahedra. The
 * surface separates inside corners (`value > threshold`) from outside ones;
 * areas are rotation-invariant, so per-axis spacing (applied to `positions`)
 * is sufficient for anisotropic and rotated grids.
 */
function tetrahedronSurfaceAreaMm2(
    positions: readonly [vec3, vec3, vec3, vec3],
    values: readonly [number, number, number, number],
    threshold: number
): number {
    const inside = [
        values[0] > threshold,
        values[1] > threshold,
        values[2] > threshold,
        values[3] > threshold,
    ];
    const insideIdx: number[] = [];
    const outsideIdx: number[] = [];
    for (let i = 0; i < 4; i++) {
        (inside[i] ? insideIdx : outsideIdx).push(i);
    }

    // Entirely inside or outside → no surface in this tet.
    if (insideIdx.length === 0 || insideIdx.length === 4) {
        return 0;
    }

    const cross = (i: number, j: number): vec3 =>
        interpolate(
            positions[i],
            positions[j],
            values[i],
            values[j],
            threshold
        );

    // One vertex on its own side → a single triangle across its 3 edges.
    if (insideIdx.length === 1 || outsideIdx.length === 1) {
        const lone = insideIdx.length === 1 ? insideIdx[0] : outsideIdx[0];
        const others = [0, 1, 2, 3].filter((i) => i !== lone);
        return triangleArea(
            cross(lone, others[0]),
            cross(lone, others[1]),
            cross(lone, others[2])
        );
    }

    // Two vs two → the surface is a quad across the four in–out edges.
    const [a, b] = insideIdx;
    const [c, d] = outsideIdx;
    const ac = cross(a, c);
    const ad = cross(a, d);
    const bc = cross(b, c);
    const bd = cross(b, d);
    return triangleArea(ac, ad, bd) + triangleArea(ac, bd, bc);
}

/**
 * Surface area in mm² from raw SDF pixel data. Marching tetrahedra over every
 * cube cell at the inside/outside `threshold`. Exported for unit testing with
 * synthetic SDFs (mirrors `_internalCalculateVolumeMm3`).
 */
export function computeSurfaceAreaMm2(
    pixels: Uint8Array[],
    resX: number,
    resY: number,
    resZ: number,
    dimX: number,
    dimY: number,
    dimZ: number,
    threshold: number = SDF_THRESHOLD
): number {
    if (dimX < 2 || dimY < 2 || dimZ < 2) {
        return 0;
    }

    let area = 0;
    const cornerValues = new Array<number>(8);
    const cornerPositions = new Array<vec3>(8);

    for (let z = 0; z < dimZ - 1; z++) {
        const slice = pixels[z];
        const sliceNext = pixels[z + 1];
        for (let y = 0; y < dimY - 1; y++) {
            for (let x = 0; x < dimX - 1; x++) {
                let minV = 255;
                let maxV = 0;
                for (let c = 0; c < 8; c++) {
                    const [dx, dy, dz] = CORNER_OFFSETS[c];
                    const v = getSdfValue(
                        dz === 0 ? slice : sliceNext,
                        x + dx,
                        y + dy,
                        dimX,
                        dimY
                    );
                    cornerValues[c] = v;
                    minV = Math.min(minV, v);
                    maxV = Math.max(maxV, v);
                }
                // Skip cells entirely inside or outside the surface — the vast
                // majority — before allocating positions or visiting any tet.
                if (minV > threshold || maxV <= threshold) {
                    continue;
                }
                for (let c = 0; c < 8; c++) {
                    const [dx, dy, dz] = CORNER_OFFSETS[c];
                    // Positions in mm (per-axis spacing). Absolute offset is
                    // irrelevant to area; rotation preserves area.
                    cornerPositions[c] = vec3.fromValues(
                        (x + dx) * resX,
                        (y + dy) * resY,
                        (z + dz) * resZ
                    );
                }

                for (const tet of CUBE_TETRAHEDRA) {
                    area += tetrahedronSurfaceAreaMm2(
                        [
                            cornerPositions[tet[0]],
                            cornerPositions[tet[1]],
                            cornerPositions[tet[2]],
                            cornerPositions[tet[3]],
                        ],
                        [
                            cornerValues[tet[0]],
                            cornerValues[tet[1]],
                            cornerValues[tet[2]],
                            cornerValues[tet[3]],
                        ],
                        threshold
                    );
                }
            }
        }
    }

    return area;
}

/**
 * Volume-weighted center of mass in VOXEL-center coordinates (uniform voxel
 * density, so this is the mean inside-voxel center). Null when no voxel is
 * inside. Exported for unit testing; the patient-mm value is obtained by
 * transforming through the grid.
 */
export function computeCenterOfMassVoxel(
    pixels: Uint8Array[],
    dimX: number,
    dimY: number,
    dimZ: number,
    threshold: number = SDF_THRESHOLD
): vec3 | null {
    let sumX = 0;
    let sumY = 0;
    let sumZ = 0;
    let count = 0;
    for (let z = 0; z < dimZ; z++) {
        const slice = pixels[z];
        for (let y = 0; y < dimY; y++) {
            const row = y * dimX;
            for (let x = 0; x < dimX; x++) {
                if (slice[row + x] > threshold) {
                    sumX += x + 0.5;
                    sumY += y + 0.5;
                    sumZ += z + 0.5;
                    count++;
                }
            }
        }
    }
    if (count === 0) {
        return null;
    }
    return vec3.fromValues(sumX / count, sumY / count, sumZ / count);
}

/**
 * Sphericity ψ = π^(1/3)·(6V)^(2/3) / SA. Null when V or SA is non-positive
 * (e.g. an empty or degenerate ROI).
 */
export function computeSphericity(
    volumeMm3: number,
    surfaceAreaMm2: number
): number | null {
    if (volumeMm3 <= 0 || surfaceAreaMm2 <= 0) {
        return null;
    }
    const numerator = Math.pow(Math.PI, 1 / 3) * Math.pow(6 * volumeMm3, 2 / 3);
    return numerator / surfaceAreaMm2;
}

/**
 * Aggregate the additional contour statistics (SA / sphericity / COM) for an
 * ROI. `volumeMm3` and `boundingBox` are passed in by the caller: volume has
 * its own cached getter (`ContourTexture.volumeCm3`) and feeds sphericity, and
 * the bounding box reuses `ContourTexture.contentBoundingBox` rather than
 * re-deriving the voxel-box transform here.
 */
export function calculateContourStatistics(
    grid: Grid,
    sdf: SignedDistanceField,
    volumeMm3: number,
    boundingBox: { min: vec3; max: vec3 } | null
): RoiStatistics {
    const [dimX, dimY, dimZ] = sdf.size;
    const surfaceAreaMm2 = computeSurfaceAreaMm2(
        sdf.pixels,
        grid.resX,
        grid.resY,
        grid.resZ,
        dimX,
        dimY,
        dimZ
    );
    const voxelCom = computeCenterOfMassVoxel(sdf.pixels, dimX, dimY, dimZ);
    return {
        surfaceAreaMm2,
        sphericity: computeSphericity(volumeMm3, surfaceAreaMm2),
        // voxelToWorld is affine, so transforming the voxel centroid yields the
        // patient-space centroid (anisotropy + orientation handled here).
        centerOfMassMm: voxelCom ? grid.toWorldSpace(voxelCom) : null,
        boundingBox,
    };
}
